import { apiFetch, setApiLogHook } from './apiFetch';
// ============================================================
// LOGGER - Unified observability for Freemium Frontend
// Single system: in-memory Live buffer + Redis (24h) + S3 (30d)
// ============================================================

type LogType = 'api' | 'nav' | 'action' | 'debug' | 'info' | 'warn' | 'error';

interface NavContext {
  nav1: string | null;  // SaaS actif (search, chatgpt, __team__...)
  nav2: string | null;  // projectId
  nav3: string | null;  // panel actif (chat, editor, drive, mail, project-picker, agent-creator)
  nav4: string | null;  // agentId
}

interface LogEntry {
  ts: string;
  type: LogType;
  user: string | null;
  sess: string;
  nav: string;
  src: string;
  method?: string;
  endpoint?: string;
  status?: number;
  ms?: number;
  action?: string;
  msg?: string;
  data?: unknown;
  stack?: string;
}

/** Simplified entry for Live tab display */
interface LiveEntry {
  id: number;
  ts: number;
  type: LogType;
  src: string;
  message: string;
}

type LiveListener = (entries: LiveEntry[], latest: LiveEntry | null) => void;

let _liveId = 0;

class Logger {
  private static instance: Logger;
  private buffer: LogEntry[] = [];
  private flushInterval = 5000;
  private _flushTimer: ReturnType<typeof setInterval> | null = null;
  private session: string;
  private sessionShort: string;
  private navContext: NavContext = { nav1: null, nav2: null, nav3: null, nav4: null };
  private userId: string | null = null;
  private endpoint = '/api/logs';
  private enabled = true;
  private sessionEndLogged = false;

  // Live buffer for in-memory display (replaces ActivityLog)
  private _liveEntries: LiveEntry[] = [];
  private _liveListeners = new Set<LiveListener>();
  private _latest: LiveEntry | null = null;
  private _clearLatestTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly MAX_LIVE = 500;

  private constructor() {
    this.session = this.generateSessionId();
    this.sessionShort = this.session.substring(0, 8);
    this.setupFlush();
    this.setupGlobalErrorHandlers();
    this.setupClickTracking();
    this.setupConsoleCapture();
    this.setupLifecycleTracking();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private generateSessionId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private getTimestamp(): string {
    const now = new Date();
    const h = String(now.getHours()).padStart(2, '0');
    const m = String(now.getMinutes()).padStart(2, '0');
    const s = String(now.getSeconds()).padStart(2, '0');
    const ms = String(now.getMilliseconds()).padStart(3, '0');
    return `${h}:${m}:${s}.${ms}`;
  }

  private getNavString(): string {
    const parts: string[] = [];
    if (this.navContext.nav1) parts.push(this.navContext.nav1);
    if (this.navContext.nav2) parts.push(this.navContext.nav2.substring(0, 8));
    if (this.navContext.nav3) parts.push(this.navContext.nav3);
    if (this.navContext.nav4) parts.push(this.navContext.nav4.substring(0, 8));
    return parts.join('.') || '-';
  }

  private createBaseEntry(type: LogType, src: string): LogEntry {
    return {
      ts: this.getTimestamp(),
      type,
      user: this.userId,
      sess: this.sessionShort,
      nav: this.getNavString(),
      src,
    };
  }

  // ============ LIVE BUFFER (replaces ActivityLog) ============

  /** Push an entry to the live in-memory buffer for UI display */
  private _pushLive(entry: LogEntry) {
    const msg = entry.msg || entry.action || '';
    if (!msg) return;
    const prefix = entry.type === 'error' ? '[ERR] '
      : entry.type === 'warn' ? '[WARN] '
      : entry.type === 'api' ? `${entry.method} ${entry.endpoint} ${entry.status ?? ''} `
      : '';
    const live: LiveEntry = {
      id: ++_liveId,
      ts: Date.now(),
      type: entry.type,
      src: entry.src,
      message: `${prefix}${msg}`,
    };
    this._liveEntries = [live, ...this._liveEntries].slice(0, Logger.MAX_LIVE);
    this._latest = live;
    this._notifyLive();

    // Clear "latest" after 4s so StatusBar fades
    if (this._clearLatestTimer) clearTimeout(this._clearLatestTimer);
    this._clearLatestTimer = setTimeout(() => {
      this._latest = null;
      this._notifyLive();
    }, 4000);
  }

  private _notifyLive() {
    for (const fn of this._liveListeners) {
      try { fn(this._liveEntries, this._latest); } catch { /* ignore */ }
    }
  }

  /** Subscribe to live log entries (for LogsPanel Live tab + StatusBar) */
  subscribe(listener: LiveListener): () => void {
    this._liveListeners.add(listener);
    listener(this._liveEntries, this._latest);
    return () => { this._liveListeners.delete(listener); };
  }

  /** Get recent live entries */
  getLiveEntries(): LiveEntry[] {
    return this._liveEntries;
  }

  /** Clear live entries */
  clearLive() {
    this._liveEntries = [];
    this._latest = null;
    this._notifyLive();
  }

  // ============ SERIALIZATION ============

  private serializeEntry(e: LogEntry): string {
    const parts: string[] = [
      `"ts":"${e.ts}"`,
      `"type":"${e.type}"`,
      `"user":${e.user ? `"${e.user}"` : 'null'}`,
      `"sess":"${e.sess}"`,
      `"nav":"${e.nav}"`,
      `"src":"${e.src}"`,
    ];

    if (e.method !== undefined) parts.push(`"method":"${e.method}"`);
    if (e.endpoint !== undefined) parts.push(`"endpoint":"${e.endpoint}"`);
    if (e.status !== undefined) parts.push(`"status":${e.status}`);
    if (e.ms !== undefined) parts.push(`"ms":${e.ms}`);
    if (e.action !== undefined) parts.push(`"action":${JSON.stringify(e.action)}`);
    if (e.msg !== undefined) parts.push(`"msg":${JSON.stringify(e.msg)}`);
    if (e.data !== undefined) parts.push(`"data":${JSON.stringify(e.data)}`);
    if (e.stack !== undefined) parts.push(`"stack":${JSON.stringify(e.stack)}`);

    return `{${parts.join(',')}}`;
  }

  // ============ NAV CONTEXT ============

  setNav(nav: Partial<NavContext>) {
    const oldNav = this.getNavString();

    this.navContext = { ...this.navContext, ...nav };

    const newNav = this.getNavString();

    if (oldNav !== newNav) {
      const entry = this.createBaseEntry('nav', 'nav');
      this.buffer.push(entry);
    }
  }

  setNav1(v: string | null) { this.setNav({ nav1: v }); }
  setNav2(v: string | null) { this.setNav({ nav2: v }); }
  setNav3(v: string | null) { this.setNav({ nav3: v }); }
  setNav4(v: string | null) { this.setNav({ nav4: v }); }

  getNav(): NavContext { return { ...this.navContext }; }

  // ============ USER ID ============

  setUserId(id: string | null) { this.userId = id; }
  getUserId(): string | null { return this.userId; }

  // ============ LOG METHODS ============

  api(src: string, method: string, endpoint: string, status: number, ms: number, error?: string) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('api', src);
    entry.method = method;
    entry.endpoint = endpoint;
    entry.status = status;
    entry.ms = ms;
    if (error) entry.msg = error;
    this.buffer.push(entry);
    this._pushLive(entry);
  }

  action(src: string, action: string, data?: unknown) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('action', src);
    entry.action = action;
    if (data !== undefined) entry.data = data;
    this.buffer.push(entry);
    this._pushLive(entry);
  }

  debug(src: string, msg: string, data?: unknown) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('debug', src);
    entry.msg = msg;
    if (data !== undefined) entry.data = data;
    this.buffer.push(entry);
  }

  info(src: string, msg: string, data?: unknown) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('info', src);
    entry.msg = msg;
    if (data !== undefined) entry.data = data;
    this.buffer.push(entry);
    this._pushLive(entry);
  }

  warn(src: string, msg: string, data?: unknown) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('warn', src);
    entry.msg = msg;
    if (data !== undefined) entry.data = data;
    this.buffer.push(entry);
    this._pushLive(entry);
  }

  error(src: string, msg: string, data?: unknown, stack?: string) {
    if (!this.enabled) return;
    const entry = this.createBaseEntry('error', src);
    entry.msg = msg;
    if (data !== undefined) entry.data = data;
    if (stack) entry.stack = stack;
    this.buffer.push(entry);
    this._pushLive(entry);
    this.flush();
  }

  // ============ GLOBAL ERROR HANDLERS ============

  /** Describe an arbitrary thrown/rejected value with enough context to
   *  debug it server-side. String(event) gives "[object Event]" — useless;
   *  extract the event type and its target instead. */
  private describeUnknown(value: unknown): string {
    if (value === null || value === undefined) return String(value);
    if (typeof value === 'string') return value;
    if (value instanceof Error) return `${value.name}: ${value.message}`;
    if (typeof Event !== 'undefined' && value instanceof Event) {
      const t = value.target as
        | (EventTarget & { src?: unknown; url?: unknown; readyState?: unknown })
        | null;
      let target = 'no-target';
      if (t) {
        target = t.constructor?.name ?? 'unknown';
        if (t.src) target += ` src=${String(t.src).slice(0, 120)}`;
        if (t.url) target += ` url=${String(t.url).slice(0, 120)}`;
        if (t.readyState !== undefined) target += ` readyState=${String(t.readyState)}`;
      }
      return `Event(${value.type || 'unknown'}) on ${target}`;
    }
    try {
      const s = JSON.stringify(value);
      if (s && s !== '{}') return s.slice(0, 300);
    } catch { /* circular */ }
    return Object.prototype.toString.call(value);
  }

  private setupGlobalErrorHandlers() {
    window.addEventListener('error', (event) => {
      this.error('window', event.message, {
        filename: event.filename,
        line: event.lineno,
      }, event.error?.stack);
    });

    window.addEventListener('unhandledrejection', (event) => {
      const reason: unknown = event.reason;
      const err = reason instanceof Error ? reason : undefined;
      this.error('promise', err?.message ?? this.describeUnknown(reason), undefined, err?.stack);
    });
  }

  // ============ CLICK TRACKING ============

  private setupClickTracking() {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const clickable = target.closest('button, a, [role="button"], [data-track]');
      if (!clickable) return;

      const el = clickable as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const text = el.innerText?.trim().slice(0, 50) || '';
      const id = el.id || undefined;
      const dataTrack = el.getAttribute('data-track') || undefined;

      if (tag === 'a' && !dataTrack) return;

      this.action('click', dataTrack || text || tag, {
        tag,
        ...(id && { id }),
        ...(text && { text }),
      });
    }, { capture: true });
  }

  // ============ CONSOLE CAPTURE ============

  private safeStringify(value: unknown): string {
    if (value === undefined) return 'undefined';
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    if (value instanceof Error) return value.message;
    if (typeof Event !== 'undefined' && value instanceof Event) {
      // JSON.stringify(Event) === '{"isTrusted":true}' — describe it instead.
      return this.describeUnknown(value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      try {
        return String(value);
      } catch {
        return '[unserializable]';
      }
    }
  }

  private setupConsoleCapture() {
    const originalError = console.error.bind(console);
    const originalWarn = console.warn.bind(console);
    const originalLog = console.log.bind(console);
    const originalInfo = console.info.bind(console);

    console.error = (...args: unknown[]) => {
      originalError(...args);
      try {
        const msg = args.map(a => this.safeStringify(a)).join(' ').slice(0, 500);
        this.error('console', msg);
      } catch {
        // Silently fail if logging itself fails
      }
    };

    console.warn = (...args: unknown[]) => {
      originalWarn(...args);
      try {
        const msg = args.map(a => this.safeStringify(a)).join(' ').slice(0, 500);
        this.warn('console', msg);
      } catch {
        // Silently fail if logging itself fails
      }
    };

    // console.log / console.info → buffered debug entries (persisted, not Live)
    console.log = (...args: unknown[]) => {
      originalLog(...args);
      try {
        const msg = args.map(a => this.safeStringify(a)).join(' ').slice(0, 500);
        this.debug('console', msg);
      } catch {
        // Silently fail if logging itself fails
      }
    };

    console.info = (...args: unknown[]) => {
      originalInfo(...args);
      try {
        const msg = args.map(a => this.safeStringify(a)).join(' ').slice(0, 500);
        this.debug('console', msg);
      } catch {
        // Silently fail if logging itself fails
      }
    };
  }

  // ============ LIFECYCLE TRACKING ============

  /** URL sans fragment — le hash peut porter le code d'auth Keycloak. */
  private safeUrl(): string {
    return window.location.href.split('#')[0];
  }

  private setupLifecycleTracking() {
    this.action('session', 'session_start', {
      url: this.safeUrl(),
      ua: navigator.userAgent,
      lang: navigator.language,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen.width}x${window.screen.height}`,
      online: navigator.onLine,
    });

    document.addEventListener('visibilitychange', () => {
      this.action('session', `visibility_${document.visibilityState}`);
      // setupFlush's own visibilitychange listener ran first — re-flush so
      // this entry ships before the tab is potentially discarded
      if (document.visibilityState === 'hidden') this.flush();
    });

    window.addEventListener('online', () => this.info('session', 'network online'));
    window.addEventListener('offline', () => this.warn('session', 'network offline'));

    // beforeunload AND pagehide: pagehide is the reliable unload signal on
    // bfcache/mobile where beforeunload may never fire; the guard dedupes
    // when both fire on the same unload.
    const logSessionEnd = (reason: string) => {
      if (this.sessionEndLogged) return;
      this.sessionEndLogged = true;
      this.action('session', 'session_end', { url: this.safeUrl(), reason });
      this.flush(); // setupFlush's unload flush ran before this entry existed
    };
    window.addEventListener('beforeunload', () => logSessionEnd('beforeunload'));
    window.addEventListener('pagehide', () => logSessionEnd('pagehide'));
    // bfcache restore: the page came back alive after a pagehide.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted && this.sessionEndLogged) {
        this.sessionEndLogged = false;
        this.action('session', 'session_resume', { url: this.safeUrl() });
      }
    });

    window.addEventListener('popstate', () => {
      this.action('session', 'navigate', { url: this.safeUrl() });
    });
  }

  // ============ FLUSH ============

  private setupFlush() {
    this._flushTimer = setInterval(() => this.flush(), this.flushInterval);
    window.addEventListener('beforeunload', () => this.flush());
    window.addEventListener('pagehide', () => this.flush());
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') this.flush();
    });
  }

  async flush(): Promise<void> {
    if (this.buffer.length === 0 || !this.enabled) return;
    const logs = [...this.buffer];
    this.buffer = [];

    const serializedLogs = logs.map(e => this.serializeEntry(e));
    const body = `{"logs":[${serializedLogs.join(',')}]}`;

    try {
      await apiFetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
    } catch {
      this.buffer = [...logs.slice(-50), ...this.buffer.slice(-50)];
    }
  }

  setEnabled(enabled: boolean) { this.enabled = enabled; }
  getSession(): string { return this.session; }
}

// ============ EXPORTS ============

export const logger = Logger.getInstance();

// Every apiFetch call (success included) lands in the persistent log.
// status 0 = network failure before any HTTP response.
setApiLogHook((src, method, endpoint, status, ms, error) => {
  logger.api(src, method, endpoint, status, ms, error);
});

export function createLogger(src: string) {
  return {
    debug: (msg: string, data?: unknown) => logger.debug(src, msg, data),
    info: (msg: string, data?: unknown) => logger.info(src, msg, data),
    warn: (msg: string, data?: unknown) => logger.warn(src, msg, data),
    error: (msg: string, data?: unknown) => logger.error(src, msg, data),
    action: (action: string, data?: unknown) => logger.action(src, action, data),
  };
}

export const log = {
  debug: (src: string, msg: string, data?: unknown) => logger.debug(src, msg, data),
  info: (src: string, msg: string, data?: unknown) => logger.info(src, msg, data),
  warn: (src: string, msg: string, data?: unknown) => logger.warn(src, msg, data),
  error: (src: string, msg: string, data?: unknown) => logger.error(src, msg, data),
  action: (src: string, action: string, data?: unknown) => logger.action(src, action, data),
  api: (src: string, method: string, endpoint: string, status: number, ms: number, error?: string) =>
    logger.api(src, method, endpoint, status, ms, error),
  setNav: (nav: Partial<NavContext>) => logger.setNav(nav),
  setNav1: (v: string | null) => logger.setNav1(v),
  setNav2: (v: string | null) => logger.setNav2(v),
  setNav3: (v: string | null) => logger.setNav3(v),
  setNav4: (v: string | null) => logger.setNav4(v),
  getNav: () => logger.getNav(),
  setUserId: (id: string | null) => logger.setUserId(id),
  getUserId: () => logger.getUserId(),
};

export type { LogType, NavContext, LogEntry, LiveEntry };
